/**
 * AlertsRisksWidget — Active alerts and risk flags (DRR-319)
 *
 * @deprecated Use AlertsWidget from @ferp/workflow-forms/widgets instead.
 * This widget has hardcoded icon mappings and severity colors which violates DRY.
 * The generic AlertsWidget gets all display config from backend.
 *
 * FHIR data: Flag resources with risk categories.
 */

import React from 'react';
import { Group, Text, Badge, Stack, Paper, Skeleton, ThemeIcon } from '@mantine/core';
import { registerDashboardWidget, type DashboardWidgetProps } from '@ferp/workflow-forms/widgets';
import {
  IconAlertTriangle,
  IconShieldCheck,
  IconRun,
  IconFall,
  IconBrain,
  IconHeart,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

const GET_SUBJECT_ALERTS = gql`
  query GetSubjectAlerts($tenantNum: Int!, $subjectActorId: String!) {
    fhirSubjectFlags(tenantNum: $tenantNum, subjectActorId: $subjectActorId, status: ACTIVE) {
      id
      code
      category
      status
      severity
      period {
        start
        end
      }
    }
    subjectAlerts(tenantNum: $tenantNum, subjectActorId: $subjectActorId, status: ACTIVE) {
      id
      alertType
      severity
      message
      createdAt
    }
  }
`;

export interface AlertsRisksWidgetProps {
  tenantNum: number;
  subjectActorId: string;
  severityFilter?: ('critical' | 'high' | 'medium' | 'low')[];
}

interface RiskItem {
  id: string;
  code: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  icon: typeof IconAlertTriangle;
  color: string;
}

const RISK_ICONS: Record<string, typeof IconAlertTriangle> = {
  fall: IconFall,
  elopement: IconRun,
  cognitive: IconBrain,
  cardiac: IconHeart,
  default: IconAlertTriangle,
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'blue',
};

function getRiskIcon(code: string): typeof IconAlertTriangle {
  const lowerCode = code.toLowerCase();
  for (const [key, icon] of Object.entries(RISK_ICONS)) {
    if (lowerCode.includes(key)) return icon;
  }
  return IconAlertTriangle;
}

export function AlertsRisksWidget({
  tenantNum,
  subjectActorId,
  severityFilter = ['critical', 'high']
}: AlertsRisksWidgetProps) {
  const { data, loading } = useQuery(GET_SUBJECT_ALERTS, {
    variables: { tenantNum, subjectActorId },
    skip: !tenantNum || !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return (
      <Stack gap="xs">
        <Skeleton height={24} width={120} />
        <Skeleton height={32} />
        <Skeleton height={32} />
      </Stack>
    );
  }

  const flags = data?.fhirSubjectFlags ?? [];
  const alerts = data?.subjectAlerts ?? [];

  const risks: RiskItem[] = flags
    .filter((f: any) => severityFilter.includes(f.severity?.toLowerCase()))
    .map((f: any) => ({
      id: f.id,
      code: f.code,
      severity: f.severity?.toLowerCase() as RiskItem['severity'],
      icon: getRiskIcon(f.code),
      color: SEVERITY_COLORS[f.severity?.toLowerCase()] ?? 'gray',
    }));

  const activeAlerts = alerts
    .filter((a: any) => severityFilter.includes(a.severity?.toLowerCase()))
    .map((a: any) => ({
      id: a.id,
      message: a.message,
      severity: a.severity?.toLowerCase() as RiskItem['severity'],
      color: SEVERITY_COLORS[a.severity?.toLowerCase()] ?? 'gray',
    }));

  if (risks.length === 0 && activeAlerts.length === 0) {
    return (
      <Paper p="xs" withBorder radius="sm">
        <Group gap="xs">
          <ThemeIcon color="green" variant="light" size="sm">
            <IconShieldCheck size={14} />
          </ThemeIcon>
          <Text size="sm" c="dimmed">No active alerts</Text>
        </Group>
      </Paper>
    );
  }

  return (
    <Stack gap="xs">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">Alerts & Risks</Text>

      {risks.map((risk) => (
        <Paper key={risk.id} p="xs" withBorder radius="sm">
          <Group gap="xs">
            <ThemeIcon color={risk.color} variant="light" size="sm">
              <risk.icon size={14} />
            </ThemeIcon>
            <Text size="sm" fw={500}>{risk.code}</Text>
            <Badge size="xs" color={risk.color} variant="light" ml="auto">
              {risk.severity.toUpperCase()}
            </Badge>
          </Group>
        </Paper>
      ))}

      {activeAlerts.map((alert: any) => (
        <Paper key={alert.id} p="xs" withBorder radius="sm" bg={`${alert.color}.0`}>
          <Group gap="xs">
            <ThemeIcon color={alert.color} variant="filled" size="sm">
              <IconAlertTriangle size={14} />
            </ThemeIcon>
            <Text size="sm">{alert.message}</Text>
          </Group>
        </Paper>
      ))}
    </Stack>
  );
}

const AlertsRisksDashboardWidget: React.FC<DashboardWidgetProps> = ({
  tenantNum,
  subjectActorId,
  config
}) => {
  const severityFilter = (config?.severity_filter as string[]) ?? ['critical', 'high'];
  return (
    <AlertsRisksWidget
      tenantNum={tenantNum}
      subjectActorId={subjectActorId}
      severityFilter={severityFilter as AlertsRisksWidgetProps['severityFilter']}
    />
  );
};

registerDashboardWidget('alerts-risks', AlertsRisksDashboardWidget, 'fhir', 'Active alerts and risk flags');
